import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Webhook, RefreshCw, Copy, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function AdminStripeWebhooks() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/webhook`;

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/stripe/webhook-events"); setData(r.data); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("Webhook URL copied"); };
  const lastOk = data?.events?.find((e) => e.ok);

  return (
    <section className="bg-card border border-border rounded-2xl shadow-sm p-6 mb-8" data-testid="admin-stripe-webhooks">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight inline-flex items-center gap-2"><Webhook className="w-5 h-5 text-primary" /> Stripe webhook</h2>
          <p className="text-sm text-muted-foreground">Stripe must send payment events to this URL so plans activate automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && <Badge variant={data.mode === "live" ? "default" : "secondary"} data-testid="stripe-mode-badge">{data.mode === "live" ? "Live mode" : "Test mode"}</Badge>}
          {data && (data.configured
            ? <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600" data-testid="webhook-secret-badge"><CheckCircle2 className="w-3 h-3" /> Signing secret set</Badge>
            : <Badge variant="destructive" className="gap-1" data-testid="webhook-secret-badge"><XCircle className="w-3 h-3" /> Signing secret missing</Badge>)}
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="webhook-refresh"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3 mb-4">
        <code className="text-xs sm:text-sm break-all flex-1" data-testid="webhook-url">{url}</code>
        <Button variant="ghost" size="sm" onClick={copy} data-testid="webhook-copy"><Copy className="w-4 h-4" /></Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        In Stripe → Developers → Webhooks → <em>Add endpoint</em>, paste the URL above and select the events
        <code className="mx-1">checkout.session.completed</code><code className="mx-1">checkout.session.expired</code><code className="mx-1">customer.subscription.created</code><code className="mx-1">customer.subscription.updated</code><code className="mx-1">customer.subscription.deleted</code><code className="mx-1">invoice.paid</code>.
        Then copy the endpoint's <strong>signing secret</strong> (starts with <code>whsec_</code>) into the app's <code>STRIPE_WEBHOOK_SECRET</code>.
        {lastOk ? ` Last event received ${fmt(lastOk.received_at)}.` : " No valid event has been received yet."}
      </p>
      <ul className="divide-y divide-border text-sm" data-testid="webhook-events">
        {(data?.events || []).map((e, i) => (
          <li key={`${e.received_at}-${i}`} className="py-2 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 min-w-0">
              {e.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-destructive shrink-0" />}
              <span className="truncate">{e.ok ? e.type : `Rejected — ${e.reason}`}</span>
              {e.ok && e.livemode === false && <Badge variant="outline" className="text-[10px]">test</Badge>}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">{fmt(e.received_at)}</span>
          </li>
        ))}
        {data && !data.events.length && <li className="py-4 text-center text-muted-foreground text-sm">No webhook calls received yet.</li>}
      </ul>
    </section>
  );
}
